<template>
  <div class="shop-overview">
    <!-- 日期筛选 -->
    <div class="ov-toolbar">
      <label>开始日期</label>
      <input v-model="startDate" type="date" />
      <span class="sep">至</span>
      <label>结束日期</label>
      <input v-model="endDate" type="date" />
      <button class="btn primary" @click="load">📊 查询</button>
      <button class="btn" @click="loadLast7">🔄 最近 7 天</button>
    </div>

    <!-- 概览卡片 -->
    <div class="ov-grid">
      <div class="card"><div class="icon">🏪</div><div><div class="val">{{ data.merchant_count }}</div><div class="lab">入驻商家（正常 {{ data.merchant_enabled }}）</div></div></div>
      <div class="card"><div class="icon">📦</div><div><div class="val">{{ data.goods_count }}</div><div class="lab">商品总数（在售 {{ data.goods_on_sale }}）</div></div></div>
      <div class="card"><div class="icon">📋</div><div><div class="val">{{ data.order_count }}</div><div class="lab">订单数量（{{ startDate }} ~ {{ endDate }}）</div></div></div>
      <div class="card"><div class="icon">💰</div><div><div class="val money">￥{{ money(data.sales_amount) }}</div><div class="lab">销售额</div></div></div>
      <div class="card"><div class="icon">👤</div><div><div class="val">{{ data.user_count }}</div><div class="lab">商城用户总数</div></div></div>
      <div class="card"><div class="icon">⭐</div><div><div class="val">{{ data.comment_count }}</div><div class="lab">商品评价数</div></div></div>
      <div class="card"><div class="icon">🔍</div><div><div class="val">{{ data.search_count }}</div><div class="lab">搜索次数（关键词 {{ data.keyword_count }} 个）</div></div></div>
      <div class="card"><div class="icon">🏦</div><div><div class="val money">￥{{ money(data.fund_balance) }}</div><div class="lab">商家可用货款（在途 ￥{{ money(data.fund_pending) }}）</div></div></div>
    </div>

    <!-- 今日数据 -->
    <div class="today-bar">
      📅 今日（{{ today }}）：订单 <b>{{ data.today_order_count }}</b> 单，销售额 <b>￥{{ money(data.today_sales_amount) }}</b>
    </div>

    <!-- 每日趋势 -->
    <div class="panel">
      <div class="panel-title">📈 订单 / 销售额趋势</div>
      <table class="ov-table">
        <thead>
          <tr><th>日期</th><th>订单数量</th><th>销售额</th></tr>
        </thead>
        <tbody>
          <tr v-for="row in data.trend" :key="row.date">
            <td>{{ row.date }}</td>
            <td>{{ row.order_count }}</td>
            <td class="money">￥{{ money(row.sales_amount) }}</td>
          </tr>
          <tr v-if="!(data.trend || []).length"><td colspan="3" class="empty">暂无数据</td></tr>
        </tbody>
      </table>
    </div>

    <!-- 商家数据（逐商家经营明细） -->
    <div class="panel">
      <div class="panel-title">
        📊 商家数据
        <span class="panel-sub">统计范围：{{ data.start_date }} ~ {{ data.end_date }}，合计 {{ statSummary.total }} 个商家 / {{ statSummary.total_orders }} 单 / ￥{{ money(statSummary.total_sales) }}</span>
      </div>
      <table class="ov-table">
        <thead>
          <tr>
            <th>店铺</th>
            <th style="width: 90px">商品（在售）</th>
            <th style="width: 80px">订单数</th>
            <th style="width: 110px">销售额</th>
            <th style="width: 80px">退款数</th>
            <th style="width: 80px">评价数</th>
            <th style="width: 90px">浏览量</th>
            <th style="width: 120px">可用货款</th>
            <th style="width: 120px">在途货款</th>
            <th style="width: 90px">状态</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in merchantStat" :key="row.id">
            <td>
              {{ row.shop_name }}
              <span class="tag" v-if="row.is_official === 1">官方自营</span>
            </td>
            <td>{{ row.goods_count }}（{{ row.online_goods_count }}）</td>
            <td>{{ row.order_count }}</td>
            <td class="money">￥{{ money(row.sales_amount) }}</td>
            <td>{{ row.refund_count }}</td>
            <td>{{ row.comment_count }}</td>
            <td>{{ row.view_count }}</td>
            <td class="money">￥{{ money(row.fund_balance) }}</td>
            <td>￥{{ money(row.fund_pending) }}</td>
            <td>
              <span class="state" :class="row.status === 1 ? 'on' : 'off'">{{ row.status === 1 ? '正常' : '已禁用' }}</span>
            </td>
          </tr>
          <tr v-if="merchantStat.length === 0"><td colspan="10" class="empty">暂无商家数据</td></tr>
        </tbody>
      </table>
    </div>

    <!-- 商家排行 -->
    <div class="panel">
      <div class="panel-title">🏆 商家销售排行</div>
      <table class="ov-table">
        <thead>
          <tr><th>排名</th><th>店铺</th><th>商品数</th><th>销售额</th></tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in data.top_merchants" :key="row.id">
            <td>{{ index + 1 }}</td>
            <td>{{ row.shop_name }}<span class="tag" v-if="row.is_official === 1">官方自营</span></td>
            <td>{{ row.goods_count }}</td>
            <td class="money">￥{{ money(row.sales_amount) }}</td>
          </tr>
          <tr v-if="!(data.top_merchants || []).length"><td colspan="4" class="empty">暂无数据</td></tr>
        </tbody>
      </table>
    </div>

    <!-- 商品排行 -->
    <div class="panel">
      <div class="panel-title">🔥 商品销售排行</div>
      <table class="ov-table">
        <thead>
          <tr><th>排名</th><th>商品</th><th>销量（件）</th><th>销售额</th></tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in data.top_goods" :key="row.goods_id">
            <td>{{ index + 1 }}</td>
            <td class="goods-cell">
              <img :src="row.goods_image" alt="">
              <span>{{ row.goods_name }}</span>
            </td>
            <td>{{ row.total_num }}</td>
            <td class="money">￥{{ money(row.sales_amount) }}</td>
          </tr>
          <tr v-if="!(data.top_goods || []).length"><td colspan="4" class="empty">暂无数据</td></tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getShopOverviewApi, getMerchantStatApi } from '@/api/merchant'

export default {
  name: 'ShopOverview',
  data () {
    return {
      startDate: '',
      endDate: '',
      merchantStat: [],
      statSummary: {
        total: 0,
        total_orders: 0,
        total_sales: 0
      },
      data: {
        merchant_count: 0,
        merchant_enabled: 0,
        goods_count: 0,
        goods_on_sale: 0,
        order_count: 0,
        sales_amount: 0,
        today_order_count: 0,
        today_sales_amount: 0,
        user_count: 0,
        comment_count: 0,
        search_count: 0,
        keyword_count: 0,
        fund_balance: 0,
        fund_pending: 0,
        trend: [],
        top_merchants: [],
        top_goods: []
      }
    }
  },
  computed: {
    today () {
      return this.formatDate(new Date())
    }
  },
  created () {
    this.loadLast7()
  },
  methods: {
    formatDate (d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    money (val) {
      const num = Number(val) || 0
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    loadLast7 () {
      const now = new Date()
      const start = new Date(now.getTime() - 6 * 24 * 3600 * 1000)
      this.startDate = this.formatDate(start)
      this.endDate = this.formatDate(now)
      this.load()
    },
    async load () {
      if (!this.startDate || !this.endDate) {
        alert('请选择开始与结束日期')
        return
      }
      try {
        const { data } = await getShopOverviewApi(this.startDate, this.endDate)
        if (data) {
          this.data = data
        }
      } catch (e) {
        console.error('商城数据加载失败', e)
      }
      // 商家数据（逐商家经营明细）
      try {
        const { data } = await getMerchantStatApi(this.startDate, this.endDate)
        this.merchantStat = (data && data.list) || []
        this.statSummary = {
          total: (data && data.total) || 0,
          total_orders: (data && data.total_orders) || 0,
          total_sales: (data && data.total_sales) || 0
        }
      } catch (e) {
        console.error('商家数据加载失败', e)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.shop-overview {
  .ov-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 14px;
    font-size: 13px;
    color: #666;

    input { border: 1px solid #e2e2e2; border-radius: 6px; padding: 6px 8px; outline: none; }
    .sep { color: #bbb; }

    .btn {
      border: 1px solid #ddd;
      background: #fff;
      border-radius: 6px;
      height: 32px;
      padding: 0 14px;
      cursor: pointer;
      color: #666;

      &.primary { background: linear-gradient(90deg, #ff6b35, #ff8c42); color: #fff; border: none; }
    }
  }

  .ov-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px;
    margin-bottom: 14px;

    .card {
      background: #fff;
      border-radius: 10px;
      padding: 16px;
      display: flex;
      align-items: center;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

      .icon {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        background: #f5f7fa;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-right: 12px;
      }

      .val { font-size: 20px; font-weight: 700; color: #222; &.money { color: #ff6b35; } }
      .lab { margin-top: 4px; font-size: 12px; color: #999; }
    }
  }

  .today-bar {
    background: #fff7f0;
    border: 1px dashed #ffd2b0;
    color: #b06a00;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;

    b { color: #ff6b35; }
  }

  .panel {
    background: #fff;
    border-radius: 10px;
    padding: 14px;
    margin-bottom: 14px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

    .panel-title { font-weight: 600; margin-bottom: 10px; }

    .panel-sub {
      margin-left: 10px;
      font-size: 12px;
      font-weight: 400;
      color: #9ca3af;
    }
  }

  .ov-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td { padding: 9px 8px; border-bottom: 1px solid #f2f2f2; text-align: left; }
    th { color: #888; font-weight: 500; background: #fafafa; }
    .money { color: #ff6b35; font-weight: 600; }
    .empty { text-align: center; color: #bbb; padding: 18px 0; }

    .tag {
      margin-left: 6px;
      font-size: 11px;
      color: #ff6b35;
      border: 1px solid #ffd2b0;
      border-radius: 4px;
      padding: 0 4px;
    }

    .goods-cell {
      display: flex;
      align-items: center;
      gap: 8px;

      img { width: 34px; height: 34px; object-fit: cover; border-radius: 6px; }
    }

    .state {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 10px;
      font-size: 12px;

      &.on { background: #e8f5e9; color: #43a047; }
      &.off { background: #fdecea; color: #e53935; }
    }
  }
}
</style>
